import { useMemo } from 'react'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import { SingleSelectFilter } from 'components/Filters/fields/SingleSelectFilter'
import {
  createFilterColumnHelper,
  DataTypeColumn,
} from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { FilterIdentifier } from 'models/saved_view'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useUserOptions } from 'services/users'

type DealNoteFilterColumnKey =
  | 'created'
  | 'modified'
  | 'user'
  | 'has_attachment'

const DEAL_NOTES_FILTER_COLUMNS: Record<
  DealNoteFilterColumnKey,
  DataTypeColumn
> = {
  created: { header: 'Date Added', group: 'Dates' },
  modified: { header: 'Date Modified', group: 'Dates' },
  user: { header: 'User', group: 'Note Details' },
  has_attachment: { header: 'Attachments', group: 'Note Details' },
}

interface DealNotesFiltersetProps {
  filterIdentifier: FilterIdentifier
}

/** Filter modal for the deal notes tab with date, user, and attachment filters. */
export default function DealNotesFilterset({
  filterIdentifier,
}: DealNotesFiltersetProps) {
  const { data: rawUserOptions } = useUserOptions({
    onlyActive: true,
    withNoneOption: true,
  })

  const userOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawUserOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawUserOptions])

  const filterStore = filterStoreRepo.getStore(filterIdentifier)
  const filterHelper = createFilterColumnHelper({
    columns: DEAL_NOTES_FILTER_COLUMNS,
  })

  return (
    <TableLinkStyleProvider variant="plain">
      <FilterModal
        store={filterStore}
        identifier={filterIdentifier}
        buttonText="Filter"
      >
        <FilterGroup title="Date Filters">
          <DateRangeFilter
            {...filterHelper.retrieveProps('created')}
            filterStore={filterStore}
          />
          <DateRangeFilter
            {...filterHelper.retrieveProps('modified')}
            filterStore={filterStore}
          />
        </FilterGroup>
        <FilterGroup title="Note Attributes">
          <SelectFilter
            {...filterHelper.retrieveProps('user')}
            options={userOptions || []}
            filterStore={filterStore}
          />
          <SingleSelectFilter
            title={DEAL_NOTES_FILTER_COLUMNS.has_attachment.header}
            filterKey="has_attachment"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            filterStore={filterStore}
            hideSearch
          />
        </FilterGroup>
      </FilterModal>
    </TableLinkStyleProvider>
  )
}
